/**
 * Canonical blob store for audio clip data.
 *
 * Design principles:
 *   1. Single source of truth — one Map for raw bytes, one for decoded buffers.
 *   2. Blobs are immutable after creation — never removed except on project unload.
 *   3. No raw bytes in Zustand — AudioClip no longer carries rawData.
 *   4. No fallback chains — every consumer reads from this store, full stop.
 *   5. Stash-first guarantee — callers must stashBlob() BEFORE addAudioClip().
 *   6. Decode is derived — AudioBuffer is a cache, regenerable from the blob.
 */

// ── Canonical blob store (raw bytes) ────────────────────────────────

const blobMap = new Map<number, Uint8Array>();

export function stashBlob(clipId: number, data: Uint8Array): void {
  blobMap.set(clipId, data);
}

export function getBlob(clipId: number): Uint8Array | undefined {
  return blobMap.get(clipId);
}

export function hasBlob(clipId: number): boolean {
  return blobMap.has(clipId);
}

/** Clear all blobs — project unload only. */
export function clearAllBlobs(): void {
  blobMap.clear();
  // Revoke all tracked blob URLs to free browser resources
  for (const url of blobUrlMap.values()) {
    URL.revokeObjectURL(url);
  }
  blobUrlMap.clear();
}
// NO removeBlob() — blobs persist until project unload (undo safety)

// ── Blob URL tracking (prevents leak under heavy TTS use) ────────────

const blobUrlMap = new Map<number, string>();

/** Track a blob URL for a clip so it can be revoked later. */
export function setBlobUrl(clipId: number, url: string): void {
  // Revoke any previous URL for this clip before replacing
  const prev = blobUrlMap.get(clipId);
  if (prev) URL.revokeObjectURL(prev);
  blobUrlMap.set(clipId, url);
}

/** Revoke and remove a tracked blob URL for a clip. */
export function revokeBlobUrl(clipId: number): void {
  const url = blobUrlMap.get(clipId);
  if (url) {
    URL.revokeObjectURL(url);
    blobUrlMap.delete(clipId);
  }
}

// ── Decode cache (AudioBuffer + peaks) ──────────────────────────────

interface BufferCacheEntry {
  audioBuffer: AudioBuffer;
  peaks: Float32Array;
}

const bufferCache = new Map<number, BufferCacheEntry>();

export function cacheBuffer(clipId: number, audioBuffer: AudioBuffer, peaks: Float32Array): void {
  bufferCache.set(clipId, { audioBuffer, peaks });
}

export function getBuffer(clipId: number): BufferCacheEntry | undefined {
  return bufferCache.get(clipId);
}

/** Clear all cached AudioBuffers — project unload only. */
export function clearAllBuffers(): void {
  bufferCache.clear();
}

/** Drop one clip's cached AudioBuffer. Used by the same-project reload to evict exactly the
 *  clips whose bytes changed, so their re-decode can't be served the stale buffer. Safe on any
 *  id: the decode is a cache, regenerable from the blob.
 *  Also deregisters any in-flight decode: that decode captured the OLD bytes, and without this it
 *  would write its result back into the cache after the eviction (see ensureDecoded). */
export function releaseBuffer(clipId: number): void {
  bufferCache.delete(clipId);
  pending.delete(clipId);
}

/** Ids with a cached decode. The reload sweeps this for buffers no longer backed by a clip row. */
export function bufferCacheIds(): number[] {
  return [...bufferCache.keys()];
}

// ── High-level helpers ──────────────────────────────────────────────

/** In-flight decode promises — prevents concurrent double-decode of the same clip. */
const pending = new Map<number, Promise<BufferCacheEntry | null>>();

/** Decode blob → AudioBuffer, cache result. Returns cached if available. Deduplicates concurrent calls. */
export async function ensureDecoded(clipId: number): Promise<BufferCacheEntry | null> {
  const cached = bufferCache.get(clipId);
  if (cached) return cached;

  const inflight = pending.get(clipId);
  if (inflight) return inflight;

  const raw = blobMap.get(clipId);
  if (!raw) return null;

  // Declared ahead of the body so the body can check whether it is still the registered decode.
  // Assigned on the next line, long before anything awaits — hence the definite-assignment `!`.
  let promise!: Promise<BufferCacheEntry | null>;
  promise = (async () => {
    const { decodeAudio, generatePeaks } = await import('@/audio/audioProcessor.ts');
    const audioBuffer = await decodeAudio(raw);
    const peaks = generatePeaks(audioBuffer);
    const entry: BufferCacheEntry = { audioBuffer, peaks };
    // Only cache while this decode is still the registered one. releaseBuffer deregisters it when
    // the clip's bytes are replaced mid-decode, and `raw` above is the bytes that were replaced —
    // caching it then would resurrect exactly the stale buffer the eviction removed. The caller
    // that asked for this decode still gets its result.
    if (pending.get(clipId) === promise) bufferCache.set(clipId, entry);
    return entry;
  })();

  pending.set(clipId, promise);
  try {
    return await promise;
  } finally {
    // Same ownership check: an evicted decode must not deregister the replacement started after it.
    if (pending.get(clipId) === promise) pending.delete(clipId);
  }
}

/** Diagnostic: log which clips have blobs vs buffers. */
export function debugAudioState(): void {
  console.log('[audioBlobStore] blobs:', [...blobMap.keys()]);
  console.log('[audioBlobStore] buffers:', [...bufferCache.keys()]);
}
